import * as React from "react";

import styles from './FilesTree.module.less'
import { IdObjectsWithId } from "../../utils";
import { FileType, ICheckedFileIds, IFile } from "../../store/files/types";
import { Button, Icon, Popconfirm, Popover, Spin, Tree } from 'antd';
import { AntTreeNodeCheckedEvent, AntTreeNodeSelectedEvent } from "antd/es/tree";
import { IAddFileAsyncRequestPayload } from "../../store/files/actions";
import { AntTreeNode } from "antd/lib/tree";
import { AddFileForm, RenamingFileForm } from "./FilesTreeForms";
import { SuperAdminZone } from "../AccessControl";
import { getFileParts } from "./constants";
import { SassIcon } from "../../utils/icons";

const { TreeNode } = Tree;

interface IOwnProps {
    files?: IdObjectsWithId<IFile>;
    root?: IFile;
    checkedFileIds: ICheckedFileIds;
}

interface ICallbackProps {
    onFetchChildren?: (id: string, resolve: (value?: any | PromiseLike<void> | undefined) => void) => void
    onAddFile?: (params: IAddFileAsyncRequestPayload) => void;
    onDeleteFile?: (file: IFile) => void;
    onRenameFile?: (file: IFile, newName: string) => void;
    onMoveFile?: (file: IFile, newParentPath: string) => void;
    onSelectFile?: (selectedKeys: string[], info: AntTreeNodeSelectedEvent) => void;
    onCheckFiles?: (params: FilesTreeCheckedHandler) => void;
    onSelectFileName?: (file: IFile) => void;
}

export type IFilesTreeProps = IOwnProps & ICallbackProps

export interface INewFileInfo {
    name: string;
    type: FileType;
}
interface IState {
    newName: string;
}

export interface FilesTreeCheckedKeys {
    checked: string[];
    halfChecked: string[];
}

export interface FilesTreeCheckedHandler {
    checkedKeys: string[] | FilesTreeCheckedKeys
    info: AntTreeNodeCheckedEvent
}

export function isCheckedKeys(obj: any): obj is FilesTreeCheckedKeys {
    return obj.checked !== undefined && obj.halfChecked !== undefined
}

class FilesTree extends React.Component<IFilesTreeProps, IState> {
    constructor(props: IFilesTreeProps) {
        super(props);
        this.state = {
            newName: ''
        };
    }

    onSelectFileName = (file: IFile) => {
        if (this.props.onSelectFileName) {
            this.props.onSelectFileName(file)
        }
    };

    onSelect = (selectedKeys: string[], info: AntTreeNodeSelectedEvent) => {
        if (this.props.onSelectFile) {
            this.props.onSelectFile(selectedKeys, info)
        }
    };

    onCheck = (checkedKeys: string[] | FilesTreeCheckedKeys, info: AntTreeNodeCheckedEvent) => {
        if (this.props.onCheckFiles) {
            this.props.onCheckFiles({ checkedKeys, info })
        }
    };

    onFetchChildren = (node: AntTreeNode) => new Promise<void>(resolve => {
        if (this.props.onFetchChildren) {
            if (node.props.eventKey) {
                this.props.onFetchChildren(node.props.eventKey, resolve)
            }
        }
    });

    onDeleteFile = (file: IFile) => {
        if (this.props.onDeleteFile) {
            this.props.onDeleteFile(file)
        }
    };

    onAddRoot = () => {
        if (this.props.onAddFile) {
            this.props.onAddFile({
                newFileInfo: { name: '/', type: FileType.FOLDER },
                isRoot: true
            });
        }
    };

    renderFile = (file: IFile | undefined) => {
        if (!file) {
            return null;
        }
        let children: any;
        if (file.children) {
            children = file.children.map((id) => {
                if (this.props.files) {
                    const child = this.props.files.byId[id];
                    if (child) {
                        return (
                            this.renderFile(child)
                        )
                    }
                }
            })
        }
        let folderType = "html";
        let fileColor = "#50666d"
        if (file && /^\/.*\/scss/.test(file.path)) {
            folderType = "scss"
            fileColor = "#b14a7e"
        } else if (file && /^\/.*\/html/.test(file.path)) {
            fileColor = "#d35936"
        }
        const { ext } = getFileParts(file.name)
        let fileIconType = "file-unknown"
        if (ext === "html") {
            fileIconType = "html5"
        } else if (ext === "md") {
            fileIconType = "file-markdown"
        }
        let fileIcon = <Icon style={{ color: fileColor }} type={fileIconType} />
        if (ext === "scss") {
            fileIcon = <SassIcon style={{ color: fileColor }} />
        }

        let deleteEle = this.renderDeleteButton(file);
        let editEle = this.renderEditButton(file);

        let title;
        let icon;
        let disableCheckbox = true;
        if (file.type === FileType.FOLDER) {
            title = <span className={styles.treeTitle}>
                {`${file.name}`}
                {this.renderAddButton(file)}
                <span className={styles.actions}>
                    {editEle}
                    {deleteEle}
                </span>
            </span>;
            icon = ({ expanded, ...rest }: { expanded: boolean }) => {
                return (<Icon style={{ color: fileColor }} type={expanded ? 'folder-open' : 'folder'} />)
            }
            if (file.path !== '/') {
                disableCheckbox = false;
            }

        } else {
            let fileName = this.renderFileName(file);
            title = <span className={styles.treeTitle}>
                <span style={{ color: fileColor }}>{fileName}</span>
                <span className={styles.actions}>
                    {editEle}
                    {deleteEle}
                </span>
            </span>;
            icon = fileIcon
        }
        return (
            <TreeNode
                title={title}
                key={file.id}
                isLeaf={file.type === FileType.FILE}
                icon={icon}
                disableCheckbox={disableCheckbox}
            >
                {children}
            </TreeNode>
        )

    };

    renderFileName = (file: IFile) => {
        return (
            <span onClick={() => this.onSelectFileName(file)}>{file.name}</span>
        )
    };

    renderEditButton = (file: IFile) => {
        const menu = this.renderRenamingMenu(file);
        return (
            <Popover
                content={menu}
                title="Title"
                trigger="click"
            >
                <Icon type="edit" />
            </Popover>
        )
    };

    renderDeleteButton = (file: IFile) => {
        if (this.props.root && file.id !== this.props.root.id) {
            return (
                <SuperAdminZone>
                    <Popconfirm
                        title={`Are you sure to delete "${file.name}"?`}
                        onConfirm={() => this.onDeleteFile(file)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Icon className="dynamic-delete-button" type="delete" />
                    </Popconfirm>
                </SuperAdminZone>
            )
        }
        return null;
    };

    renderAddButton = (file: IFile) => {
        const menu = this.renderAddMenu(file);
        return (
            <Popover
                content={menu}
                title="Add new file:"
                trigger="click"
            >
                <Icon type="plus-circle" />
            </Popover>
        )
    };

    renderAddMenu = (file: IFile) => {
        return (
            <AddFileForm file={file} onAddFile={this.props.onAddFile} />
        )
    };

    renderRenamingMenu = (file: IFile) => {
        return (
            <RenamingFileForm
                file={file}
                onRenameFile={this.props.onRenameFile}
                onMoveFile={this.props.onMoveFile}
            />
        )
    };

    render() {
        // console.log("reducers render........");
        let root = this.props.root;
        let defaultCheckedKeys: string[] = this.props.checkedFileIds.fileIds;
        let defaultExpandedKeys: string[] = this.props.checkedFileIds.fileIds;

        return (
            <div className={styles.treeContainer}>
                {root ?
                    <Tree
                        className="hide-file-icon"
                        checkStrictly={true}
                        autoExpandParent={true}
                        // defaultExpandAll={true}
                        // loadData={this.onFetchChildren}
                        showLine={true}
                        showIcon={true}
                        checkable={true}
                        defaultExpandedKeys={defaultCheckedKeys}
                        defaultCheckedKeys={defaultExpandedKeys}
                        checkedKeys={this.props.checkedFileIds.fileIds}
                        onSelect={this.onSelect}
                        onCheck={this.onCheck}
                    >
                        {
                            this.renderFile(root)
                        }
                    </Tree>
                    : <Spin />
                }
            </div>
        )
    }
}

export default FilesTree;

